import type { RequestStream } from '@talos/kyoko';
import {
  CARE_ORDER,
  ORDER,
  QUOTE,
  UpdateActionEnum,
  useCombinedErrorObs,
  useSecuritiesContext,
  useSubscription,
  useSyncedRef,
  useWSFilterPipe,
  WORKING_ORDER,
  wsScanToMap,
  type CareOrder,
  type MinimalSubscriptionResponse,
  type Order,
  type Quote,
} from '@talos/kyoko';
import { minBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { distinctUntilChanged, map, shareReplay, tap, type Observable } from 'rxjs';
import { isCareOrderRow, type CareOrderBlotterEntity, type CareOrderRow } from '../types';
import { careOrderEntitiesMerger, type CareOrderBlotterPipelineEmission } from './careOrderEntitiesMerger';

interface UseCareOrderBlotterDataObsParams {
  enableCareOrderNetting?: boolean;
  careOrderBranchFilter?: (careOrder: CareOrderRow) => boolean;
}

function getCareOrderBlotterRowID(row: CareOrderBlotterEntity) {
  return row.rowID;
}

export const useCareOrderBlotterDataObs = (params: boolean | UseCareOrderBlotterDataObsParams = false) => {
  const { enableCareOrderNetting = false, careOrderBranchFilter } =
    typeof params === 'boolean' ? { enableCareOrderNetting: params } : params;

  const { data: careOrdersObs, error: careOrderErrors } = useSubscription<CareOrder>({
    name: CARE_ORDER,
    tag: 'CareOrder blotter',
  });
  const { data: workingOrdersObs, error: workingOrderErrors } = useSubscription<CareOrder>(
    enableCareOrderNetting
      ? {
          name: WORKING_ORDER,
          tag: 'CareOrder blotter',
        }
      : null
  );

  const sharedCareOrdersObs = useMemo(
    () => careOrdersObs.pipe(shareReplay({ bufferSize: 1, refCount: true })),
    [careOrdersObs]
  );

  // First we need to get all care orders by ID...
  const careOrdersByIDObs = useMemo(
    () =>
      sharedCareOrdersObs.pipe(
        wsScanToMap({
          getUniqueKey: order => order.OrderID,
          newMapEachUpdate: false,
          waitForAllPagesToLoad: true,
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [sharedCareOrdersObs]
  );

  const [quotesRequest, setQuotesRequest] = useState<RequestStream | null>(null);
  const { data: quotesObs, error: quoteErrors } = useSubscription<Quote>(quotesRequest);

  const [ordersRequest, setOrdersRequest] = useState<RequestStream | null>(null);
  const { data: ordersObs, error: orderErrors } = useSubscription<Order>(ordersRequest);

  useEffect(() => {
    const subscription = careOrdersByIDObs
      .pipe(
        map(careOrdersByID => minBy(Array.from(careOrdersByID.values()), 'SubmitTime')?.SubmitTime),
        distinctUntilChanged()
      )
      .subscribe(startDate => {
        setQuotesRequest({
          name: QUOTE,
          tag: 'CareOrder blotter',
          StartDate: startDate,
        });
        setOrdersRequest({
          name: ORDER,
          tag: 'CareOrder blotter',
          StartDate: startDate,
        });
      });
    return () => {
      subscription.unsubscribe();
    };
  }, [careOrdersByIDObs]);

  const { securitiesBySymbol } = useSecuritiesContext();
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol); // make it stable so we dont rebuild the pipe when we get changes

  // we keep a map here of all entities we have received so far. This is used for resolving care orders as we are handling child order/quote messages
  // for filtering purposes, we need to be able to easily resolve the parent care order from the child order's parent id from within the filtering function
  const entitiesByRowIDRef = useRef(new Map<string, CareOrderBlotterEntity>());

  const filterRowByParentCareOrder = useCallback(
    (row: CareOrderBlotterEntity) => {
      if (careOrderBranchFilter == null) {
        return true;
      }

      if (row.UpdateAction === UpdateActionEnum.Remove) {
        // Removal rows must pass through so AG Grid can clear visible rows, even if the parent
        // would no longer satisfy the branch filter.
        return true;
      }

      // We only do filtering on the care order rows. They drive the visibility of the branch.
      if (isCareOrderRow(row)) {
        return careOrderBranchFilter(row);
      } else {
        // If we are not a care order row, we're like an OrderRow, or a LegRow for example. Our existence in the blotter is purely determined by
        // whether or not our parent care order is visible. So check the filtering result of our care order parent or grandparent, and return that result.
        const parentRow = entitiesByRowIDRef.current.get(row.CareOrderID);
        return isCareOrderRow(parentRow) && careOrderBranchFilter(parentRow);
      }
    },
    [careOrderBranchFilter]
  );
  const careOrderBlotterRowFilterPipe = useWSFilterPipe({
    getUniqueKey: getCareOrderBlotterRowID,
    filterFunc: filterRowByParentCareOrder,
  });

  const pipelineEmissionObs = useMemo<Observable<CareOrderBlotterPipelineEmission>>(
    () =>
      careOrderEntitiesMerger({
        enableCareOrderNetting,
        careOrdersObs: sharedCareOrdersObs,
        workingOrdersObs,
        quotesObs,
        ordersObs,
        getSecuritiesBySymbol: () => securitiesBySymbolRef.current,
      }),
    [enableCareOrderNetting, ordersObs, quotesObs, securitiesBySymbolRef, sharedCareOrdersObs, workingOrdersObs]
  );

  const unfilteredDeltaDataObs = useMemo<Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>>(
    () =>
      pipelineEmissionObs.pipe(
        tap(emission => {
          // Quickly put the new entity map into our ref (synchronously) so the filtering pipe
          // can make use of it in the subsequent step
          entitiesByRowIDRef.current = emission.entitiesByRowID;
        }),
        map(emission => emission.output),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [pipelineEmissionObs]
  );

  // Filtered deltas for the Care Order blotter grid. Branch filters (e.g. date range) apply here.
  const deltaDataObs = useMemo<Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>>(
    () => unfilteredDeltaDataObs.pipe(careOrderBlotterRowFilterPipe),
    [careOrderBlotterRowFilterPipe, unfilteredDeltaDataObs]
  );

  /**
   * Absolute unfiltered entity memory scanned from {@link unfilteredDeltaDataObs}.
   *
   * Intentionally bypasses blotter branch filters so consumers like Validate Fixing still see
   * every care-order branch in pipeline memory, not only the currently visible blotter subset.
   */
  const unfilteredFullDataObs = useMemo<Observable<Map<string, CareOrderBlotterEntity>>>(
    () =>
      unfilteredDeltaDataObs.pipe(
        wsScanToMap({
          getUniqueKey: getCareOrderBlotterRowID,
          newMapEachUpdate: false,
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
    [unfilteredDeltaDataObs]
  );

  const combinedErrorObs = useCombinedErrorObs(
    [careOrderErrors, quoteErrors, orderErrors, enableCareOrderNetting ? workingOrderErrors : undefined].filter(
      errorObs => errorObs != null
    )
  );
  return {
    dataObservable: deltaDataObs,
    // Unfiltered absolute entity map for Validate Fixing and other non-blotter consumers.
    fullDataObservable: unfilteredFullDataObs,
    errorObservable: combinedErrorObs,
  };
};
